/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import { CircularProgressbar } from 'react-circular-progressbar';
import Dashboard, { DashboardColumn, DashboardRow } from '../../dashboard';
import TopSdgs from '../../top-sdgs';
import { SDGControlStrip } from '../../sdg-control-strip';
import { useAppDispatch, useAppSelector } from '../../../reducers';
import { match, useRouteMatch } from 'react-router-dom';
import { loadPortfolioById, reloadPortfolioById } from '../../../slice/portfolioSlice';
import { SdgChartModal } from '../../impact-performance/SdgChartModal';
import PortfolioScoresRadarChart from '../../charts/portfolio-scores-radar-chart';
import { loadPortfolioCompanies, reloadPortfolioCompanies } from '../../../slice/portfolioCompanySlice';
import UpdateMaterialityModal from '../../update-materiality-modal';
import { loadScorecardsByInitiativeId, reloadScorecards } from '../../../actions/scorecard';
import { reloadPortfolioTree } from '../../../slice/portfolioTreeSlice';
import { getMainScorecard } from '../../../selectors/scorecard';
import { PortfolioInsightsSidebar } from '../../../routes/summary/insights/partials/sidebar/PortfolioInsightsSidebar';
import LoadingPlaceholder from '@components/LoaderContainer/LoadingPlaceholder';

interface SdgChartState {
  isOpen: boolean;
  sdgCode?: string | number;
}

export const Insights = () => {
  const dispatch = useAppDispatch();
  const routeMatch: match<{ portfolioId: string }> = useRouteMatch();
  const portfolioId = routeMatch.params.portfolioId;

  const { data: portfolio, loaded, loading } = useAppSelector((state) => state.portfolio);
  const scorecardState = useAppSelector((state) => getMainScorecard(state));

  const [displaySdgChartModal, setSdgChartModal] = useState<SdgChartState>({ isOpen: false });
  const [displayMaterialityModal, toggleMaterialityModal] = useState(false);

  const handleChangeSDG = (sdgCodes?: string | number | number[]) => {
    const code = Array.isArray(sdgCodes) ? sdgCodes[0] : sdgCodes;
    setSdgChartModal((m) => ({ ...m, isOpen: Boolean(code), sdgCode: code }));
  };

  useEffect(() => {
    if (!portfolioId) {
      return;
    }
    dispatch(loadPortfolioById(portfolioId));
    dispatch(loadScorecardsByInitiativeId(portfolioId));
    dispatch(loadPortfolioCompanies(portfolioId));
  }, [dispatch, portfolioId]);

  const isFullyLoaded = loaded && !loading;

  if (!isFullyLoaded || !portfolio) {
    return (
      <Dashboard className='insights-dashboard' hasSidebar={true}>
        <PortfolioInsightsSidebar portfolioId={portfolioId} />
        <LoadingPlaceholder height={600} />
      </Dashboard>
    );
  }

  const scorecard = portfolio.scorecard.scorecard;

  const handleReload = async () => {
    if (portfolioId) {
      dispatch(reloadScorecards());
      dispatch(reloadPortfolioCompanies(portfolioId));
      dispatch(reloadPortfolioById(portfolioId, true));
    }
    dispatch(reloadPortfolioTree());
  };

  return (
    <>
      <Dashboard className='insights-dashboard' hasSidebar={true}>
        <PortfolioInsightsSidebar portfolioId={portfolioId} />
        <Dashboard className='mx-0'>
          <DashboardRow title='SDG insights'>
            <DashboardColumn title='Contribution index' subtitle='Weighted-average aggregated index' flexBasisPc='50%'>
              <div className='text-right' style={{ marginTop: '-40px' }}>
                <CircularProgressbar
                  strokeWidth={10}
                  value={scorecard.actual ?? 0}
                  text={`${Math.round(scorecard.actual ?? 0)}%`}
                />
              </div>
            </DashboardColumn>
            <DashboardColumn title='Top goals' flexBasisPc='50%'>
              <div className='text-right' style={{ marginTop: '-20px' }}>
                <TopSdgs handleSDGClick={handleChangeSDG} goals={scorecard.goals} width={80} height={80} />
              </div>
            </DashboardColumn>
          </DashboardRow>
          <DashboardRow
            title='SDG materiality and contribution'
            buttons={[
              {
                icon: 'fal fa-cog',
                tooltip: 'Configure materiality',
                onClick: () => toggleMaterialityModal(true),
              },
            ]}
          >
            <SDGControlStrip
              isCompanyTrackerLight={false}
              scorecardState={scorecardState}
              initiativeId={portfolio._id}
              displayMateriality={true}
              displayProgressBars={true}
              defaultSelectedSDGs={displaySdgChartModal.sdgCode ? [displaySdgChartModal.sdgCode] : []}
              className='p-2'
              handleClick={handleChangeSDG}
            />
          </DashboardRow>
          <DashboardRow>
            {['Contribution by goal', 'Contribution by materiality'].map((item, index) => (
              <DashboardColumn key={index} className='flex-fill' title={item}>
                <div className='d-flex align-items-center justify-content-center'>
                  <PortfolioScoresRadarChart
                    portfolio={portfolio}
                    width={220}
                    height={220}
                    showMaterialityChart={!!index}
                  />
                </div>
              </DashboardColumn>
            ))}
          </DashboardRow>
        </Dashboard>
      </Dashboard>
      {displaySdgChartModal.isOpen && portfolioId && (
        <SdgChartModal
          handleChangeSDG={(code) => code && handleChangeSDG(code)}
          initiativeId={portfolioId}
          isOpen={displaySdgChartModal.isOpen}
          toggle={() => setSdgChartModal((v) => ({ ...v, sdgCode: undefined, isOpen: !v.isOpen }))}
          sdgCode={displaySdgChartModal.sdgCode}
          scorecard={{ scorecard: portfolio.scorecard.scorecard, initiative: portfolio }}
        />
      )}
      {displayMaterialityModal && portfolioId && (
        <UpdateMaterialityModal
          initiative={portfolio}
          isOpen={displayMaterialityModal}
          toggle={() => toggleMaterialityModal(!displayMaterialityModal)}
          handleReload={handleReload}
        />
      )}
    </>
  );
};
